import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '@healthdesk/shared-hooks';
import { FEATURE_GATES, TIER_DISPLAY_NAMES, tierAtLeast } from '@healthdesk/shared-utils';

/**
 * FeatureGate — wraps content that requires a minimum subscription tier.
 *
 * Props:
 *   feature (string)         — key from FEATURE_GATES config
 *   minTier (string)         — override; use if not using the config key
 *   fallback (node)          — what to render when access is denied (default: null)
 *   showUpgradePrompt (bool) — if true, renders an upgrade CTA instead of null
 *   forceAllow (bool)        — if true, always render children (e.g. public /symptomchecker)
 *   children
 */
export default function FeatureGate({
  feature,
  minTier: minTierOverride,
  fallback = null,
  showUpgradePrompt = false,
  forceAllow = false,
  children,
}) {
  const { subscriptionTier } = useAuth();

  if (forceAllow) return <>{children}</>;

  const resolvedMinTier = minTierOverride || FEATURE_GATES[feature]?.minTier || 'free';
  const hasAccess = tierAtLeast(subscriptionTier, resolvedMinTier);

  if (hasAccess) return <>{children}</>;

  if (showUpgradePrompt) {
    const tierName = TIER_DISPLAY_NAMES[resolvedMinTier] || resolvedMinTier;
    return (
      <Box
        sx={{
          p: 1.5,
          px: 2,
          bgcolor: 'grey.100',
          borderRadius: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          <Box component="span" sx={{ fontWeight: 700 }}>
            Subscription required:
          </Box>{' '}
          Upgrade to{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {tierName}
          </Box>{' '}
          to use this feature.
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          A subscription does not include clinical services.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          component={RouterLink}
          to="/dashboard/upgrade"
        >
          View Plans
        </Button>
      </Box>
    );
  }

  return fallback;
}
